import { AGENT_STAGES } from "../../../src/sandbox/agent-stages.ts";
import type { SpendUsage } from "../../../src/admin/spend.ts";
import { formatSpend, formatTokens } from "../lib/spend.ts";

// The pieces the Spend screen and a Request's spend page share: the token
// total of a row, the stage colours, a Request's name and the usage cells
// every table repeats. See ADR-0051 §6.

/** Every token a row counts: input, output, cache read and cache write. */
export function totalTokens(u: SpendUsage): number {
  return u.inputTokens + u.outputTokens + u.cacheReadTokens + u.cacheWriteTokens;
}

/** A 0–1 share as a whole percentage, such as `71%`. */
export function percent(share: number): string {
  return `${Math.round(share * 100)}%`;
}

/**
 * The colour of a stage on the trend: the stages in their fixed order take
 * the categorical slots one to seven, anything else the eighth, so a stage
 * keeps its colour whatever the range shows.
 */
export function stageColor(stage: string): string {
  const i = (AGENT_STAGES as readonly string[]).indexOf(stage);
  return `var(--viz-${i === -1 ? 8 : i + 1})`;
}

/** A Request's name on the Spend screen: its copied title, or "Deleted Request" once it is gone. */
export function requestName(title: string | null): string {
  return title ?? "Deleted Request";
}

/** The path of a Request's spend page. */
export function spendRequestPath(requestId: string): string {
  return `/spend/requests/${encodeURIComponent(requestId)}`;
}

/**
 * The four usage cells every Spend table ends with: cost (with the rows it
 * leaves out unpriced), tokens, calls and cache-hit ratio.
 */
export function UsageCells({ u, unpricedHref }: { u: SpendUsage; unpricedHref?: string }): JSX.Element {
  return (
    <>
      <td className="num">
        {formatSpend(u.costMicros)}
        {u.unpricedCalls > 0 ? (
          <span className="sub">
            {unpricedHref ? <a href={unpricedHref}>{u.unpricedCalls} unpriced</a> : `${u.unpricedCalls} unpriced`}
          </span>
        ) : null}
      </td>
      <td className="num" title={`in ${u.inputTokens} · out ${u.outputTokens} · cache read ${u.cacheReadTokens} · cache write ${u.cacheWriteTokens}`}>
        {formatTokens(totalTokens(u))}
      </td>
      <td className="num">{u.calls}</td>
      <td className="num">{percent(u.cacheHitRatio)}</td>
    </>
  );
}

/** The header cells over `UsageCells`. */
export function UsageHeads(): JSX.Element {
  return (
    <>
      <th className="num">Cost</th>
      <th className="num">Tokens</th>
      <th className="num">Calls</th>
      <th className="num">Cache hit</th>
    </>
  );
}
